import { useMemo, useState } from "react";
import type { EmailOtpType } from "@supabase/supabase-js";
import { supabase } from "../../services/supabase";
import { Button, InlineNotification, PasswordInput } from "@carbon/react";
import { Layout } from "../../layouts";
import { establishApiSession } from "../../services/apiSession";
import { getSafeRedirectTarget } from "../../utils/redirect";

import styles from "./SetPasswordPage.module.scss";

const SITE_DOMAIN = import.meta.env.VITE_SITE_DOMAIN;
const AUTH_DOMAIN = import.meta.env.VITE_AUTH_DOMAIN;

type LinkAuth =
  | {
      kind: "token_hash";
      tokenHash: string;
      type: EmailOtpType;
    }
  | {
      kind: "code";
      code: string;
    }
  | {
      kind: "session";
      accessToken: string;
      refreshToken: string;
    };

function getLinkAuth(): LinkAuth | null {
  // Support email token hashes, PKCE codes, and older links carrying session tokens in the fragment.
  const url = new URL(window.location.href);
  const tokenHash = url.searchParams.get("token_hash");
  const type = url.searchParams.get("type");

  if (tokenHash && (type === "invite" || type === "recovery")) {
    return { kind: "token_hash", tokenHash, type };
  }

  const code = url.searchParams.get("code");
  if (code) {
    return { kind: "code", code };
  }

  const hash = new URLSearchParams(window.location.hash.slice(1));
  const hashType = hash.get("type");
  const accessToken = hash.get("access_token");
  const refreshToken = hash.get("refresh_token");

  if ((hashType === "invite" || hashType === "recovery") && accessToken && refreshToken) {
    return { kind: "session", accessToken, refreshToken };
  }

  return null;
}

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "Failed to set password.";
}

export default function SetPasswordPage() {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [email, setEmail] = useState<string | null>(null);
  const [linkAuthenticated, setLinkAuthenticated] = useState(false);
  const [passwordUpdated, setPasswordUpdated] = useState(false);

  const linkAuth = useMemo(() => getLinkAuth(), []);
  const redirectTo = useMemo(() => {
    const url = new URL(window.location.href);
    return getSafeRedirectTarget(url.searchParams.get("redirect_to") || SITE_DOMAIN);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!linkAuth) return setError("This link is invalid or expired.");
    if (!passwordUpdated) {
      if (pw.length < 8) return setError("Password must be at least 8 characters.");
      if (pw !== pw2) return setError("Passwords do not match.");
    }

    setSubmitting(true);
    try {
      // Consume the link on submission, then remember success so a later retry does not reuse it.
      if (!linkAuthenticated) {
        if (linkAuth.kind === "token_hash") {
          const { error } = await supabase.auth.verifyOtp({
            token_hash: linkAuth.tokenHash,
            type: linkAuth.type,
          });
          if (error) throw error;
        } else if (linkAuth.kind === "code") {
          const { error } = await supabase.auth.exchangeCodeForSession(linkAuth.code);
          if (error) throw error;
        } else {
          const { error } = await supabase.auth.setSession({
            access_token: linkAuth.accessToken,
            refresh_token: linkAuth.refreshToken,
          });
          if (error) throw error;
        }

        setLinkAuthenticated(true);
      }

      // Confirm the authenticated user before changing their password.
      const { data, error: getUserErr } = await supabase.auth.getUser();
      if (getUserErr) throw getUserErr;
      if (!data.user) throw new Error("This link is invalid or expired.");
      setEmail(data.user.email ?? null);

      // If cookie creation failed on a previous attempt, retry that step without changing the password again.
      if (!passwordUpdated) {
        const { error } = await supabase.auth.updateUser({ password: pw });
        if (error) throw error;

        setPasswordUpdated(true);
      }

      // Establish the website/API cookie session before leaving the password form.
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      if (!token) throw new Error("Missing session after password update.");

      await establishApiSession(token);

      window.location.href = redirectTo;
    } catch (e: unknown) {
      setError(getErrorMessage(e));
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className={styles.page}>
        <h2 className={styles.page__header}>Set your password</h2>
        {email && <p className={styles.page__email}>{email}</p>}
        {error && (
          <InlineNotification
            kind="error"
            title="Couldn't set password"
            subtitle={error ?? " "}
            lowContrast
            className={styles.page__errorNotice}
            hideCloseButton
          />
        )}
        <form onSubmit={handleSubmit}>
          <PasswordInput
            id="new-password"
            labelText="New password"
            value={pw}
            onChange={(e) => setPw((e.target as HTMLInputElement).value)}
            autoComplete="new-password"
            required
            className={styles.page__newPasswordInput}
          />
          <PasswordInput
            id="confirm-password"
            labelText="Confirm password"
            value={pw2}
            onChange={(e) => setPw2((e.target as HTMLInputElement).value)}
            autoComplete="new-password"
            required
            className={styles.page__confirmPasswordInput}
          />

          <div className={styles.page__buttons}>
            <Button type="submit" disabled={submitting}>
              {submitting ? "Saving…" : "Save password"}
            </Button>
            <Button
              kind="tertiary"
              type="button"
              disabled={submitting}
              onClick={() => (window.location.href = AUTH_DOMAIN)}
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
